import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, CheckCircle, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    shopName: '',
    city: '',
    state: '',
    phone: '',
    email: '',
    website: '',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // This is a mock submission - in production, this would connect to your backend
    console.log('Form submitted:', formData);
    setSubmitted(true);
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setSubmitted(false);
      setFormData({
        name: '',
        shopName: '',
        city: '',
        state: '',
        phone: '',
        email: '',
        website: '',
        message: ''
      });
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-950">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Book Your <span className="text-orange-500">Free Call</span>
            </h1>
            <p className="text-xl text-gray-400">
              No pressure. No tech talk. Just practical ideas for your shop.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <Card className="bg-gray-900 border-gray-800">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold text-white mb-6">
                    Get Started Today
                  </h2>
                  <p className="text-gray-400 mb-8">
                    Fill out the form below and we'll reach out within 24 hours to schedule your free discovery call.
                  </p>

                  {submitted ? (
                    <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-8 text-center">
                      <CheckCircle className="h-16 w-16 text-orange-500 mx-auto mb-4" />
                      <h3 className="text-2xl font-bold text-white mb-2">Thank You!</h3>
                      <p className="text-gray-300">
                        We've received your information and will contact you within 24 hours.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div>
                        <Label htmlFor="name" className="text-gray-300 mb-2 block">Your Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          className="bg-gray-800 border-gray-700 text-white focus:border-orange-500"
                          placeholder="John Smith"
                        />
                      </div>

                      <div>
                        <Label htmlFor="shopName" className="text-gray-300 mb-2 block">Shop Name *</Label>
                        <Input
                          id="shopName"
                          name="shopName"
                          type="text"
                          required
                          value={formData.shopName}
                          onChange={handleChange}
                          className="bg-gray-800 border-gray-700 text-white focus:border-orange-500"
                          placeholder="Smith's Auto Repair"
                        />
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="city" className="text-gray-300 mb-2 block">City *</Label>
                          <Input
                            id="city"
                            name="city"
                            type="text"
                            required
                            value={formData.city}
                            onChange={handleChange}
                            className="bg-gray-800 border-gray-700 text-white focus:border-orange-500"
                            placeholder="Austin"
                          />
                        </div>
                        <div>
                          <Label htmlFor="state" className="text-gray-300 mb-2 block">State *</Label>
                          <Input
                            id="state"
                            name="state"
                            type="text"
                            required
                            value={formData.state}
                            onChange={handleChange}
                            className="bg-gray-800 border-gray-700 text-white focus:border-orange-500"
                            placeholder="TX"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="phone" className="text-gray-300 mb-2 block">Phone Number *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          className="bg-gray-800 border-gray-700 text-white focus:border-orange-500"
                          placeholder="(555) 123-4567"
                        />
                      </div>

                      <div>
                        <Label htmlFor="email" className="text-gray-300 mb-2 block">Email Address *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className="bg-gray-800 border-gray-700 text-white focus:border-orange-500"
                          placeholder="john@smithsauto.com"
                        />
                      </div>

                      <div>
                        <Label htmlFor="website" className="text-gray-300 mb-2 block">Current Website (Optional)</Label>
                        <Input
                          id="website"
                          name="website"
                          type="url"
                          value={formData.website}
                          onChange={handleChange}
                          className="bg-gray-800 border-gray-700 text-white focus:border-orange-500"
                          placeholder="www.yourshop.com"
                        />
                      </div>

                      <div>
                        <Label htmlFor="message" className="text-gray-300 mb-2 block">Tell Us About Your Shop (Optional)</Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          className="bg-gray-800 border-gray-700 text-white focus:border-orange-500 min-h-32"
                          placeholder="What services do you offer? What challenges are you facing?"
                        />
                      </div>

                      <Button 
                        type="submit" 
                        size="lg" 
                        className="w-full bg-orange-500 hover:bg-orange-600 text-white py-6 text-lg font-semibold transition-all duration-300 hover:scale-105"
                      >
                        Book My Free Call
                        <Send className="ml-2 h-5 w-5" />
                      </Button>

                      <p className="text-gray-500 text-sm text-center">
                        By submitting this form, you agree to receive communications from Firebyte Websites. You can unsubscribe at any time.
                      </p>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Contact Information */}
            <div>
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-white mb-6">
                  What Happens Next?
                </h2>
                <div className="space-y-6">
                  {[
                    { step: "1", title: "We Review Your Info", desc: "We'll look at your shop and prepare some ideas" },
                    { step: "2", title: "We Reach Out", desc: "We'll call or email to schedule a 30-minute call" },
                    { step: "3", title: "Discovery Call", desc: "We'll discuss your challenges and show you solutions" },
                    { step: "4", title: "Custom Proposal", desc: "If it's a good fit, we'll create a plan for your shop" }
                  ].map((item, idx) => (
                    <div key={idx} className="flex gap-4">
                      <div className="bg-orange-500 text-white w-10 h-10 rounded-lg flex items-center justify-center font-bold flex-shrink-0">
                        {item.step}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white mb-1">{item.title}</h3>
                        <p className="text-gray-400">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact Cards */}
              <div className="space-y-4">
                <Card className="bg-gray-900 border-gray-800">
                  <CardContent className="p-6 flex items-center gap-4">
                    <div className="bg-orange-500/10 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="h-6 w-6 text-orange-500" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Call Us</p>
                      <a href="tel:+15551234567" className="text-white font-semibold text-lg hover:text-orange-500 transition-colors">
                        (555) 123-4567
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gray-900 border-gray-800">
                  <CardContent className="p-6 flex items-center gap-4">
                    <div className="bg-orange-500/10 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="h-6 w-6 text-orange-500" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Email Us</p>
                      <a href="mailto:hello@firebytewebsites.com" className="text-white font-semibold text-lg hover:text-orange-500 transition-colors">
                        hello@firebytewebsites.com
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gray-900 border-gray-800">
                  <CardContent className="p-6 flex items-center gap-4">
                    <div className="bg-orange-500/10 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="h-6 w-6 text-orange-500" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Response Time</p>
                      <p className="text-white font-semibold text-lg">Within 24 Hours</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gray-900 border-gray-800">
                  <CardContent className="p-6 flex items-center gap-4">
                    <div className="bg-orange-500/10 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-6 w-6 text-orange-500" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Serving</p>
                      <p className="text-white font-semibold text-lg">Automotive Shops Across the USA</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Reassurance */}
              <Card className="mt-6 bg-orange-500/10 border-orange-500/30">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-orange-500 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-white font-bold mb-2">No Pressure, No Obligation</h3>
                      <p className="text-gray-300 text-sm">
                        This is just a conversation. We'll give you honest advice even if we're not the right fit for your shop.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
